<template>
  <div class="input-items">
    <a-select
      v-if="showSelector"
      :placeholder="placeholder"
      :value="newItem"
      :disabled="disabled"
      :options="options"
      :showArrow="false"
      :filter-option="false"
      @select="addNewItem"
      @search="searchItems"
      @input-key-down="onPressKey"
      show-search
    />
    <a-flex class="tags_container" v-if="items.length || !showSelector">
      <slot name="selected-header" />
      <a-flex class="tags_list">
        <tag-item-with-ellipsis
          v-for="item in items"
          :class="`tag${disabled ? '' : ' tag-enabled'}`"
          :key="'udc-' + item.notation"
          @click="removeItem(item)"
        >
          {{ `${item.notation}. ${item.caption}` }}
        </tag-item-with-ellipsis>
      </a-flex>
    </a-flex>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import { debounce } from 'lodash-es';
  import { useApi } from '@/api/use-api';
  import TagItemWithEllipsis from '@/components/tags/TagItemWithEllipsis.vue';

  const props = defineProps({
    items: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Задайте научные области',
    },
    /**
     * @member props.projectMode Если задан ИД проекта, то по умолчанию в дропдауне отображаются
     * УДК портфелей этого проекта, если портфели уже заданы.
     */
    projectId: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showSelector: {
      type: Boolean,
      default: true,
    },
  });

  const api = useApi();
  const emit = defineEmits(['update', 'delete']);
  const newItem = ref(null);
  const options = ref([]);
  const searchInput = ref('');
  const isLoading = ref(true);

  onMounted(async () => {
    if (props.showSelector) {
      await searchItems();
    }
    isLoading.value = false;
  });

  const removeItem = (value) => {
    if (!props.disabled) {
      const newList = props.items.filter((el) => {
        return el.id !== value.id;
      });
      emit('update', newList);
      emit('delete', value.id);
    }
  };

  const onPressKey = (event) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();

      const newItemName = searchInput.value?.trim();
      if (newItemName !== '' && options.value.length === 0) {
        addNewItem(newItemName);
      }
      searchInput.value = '';
    }
  };

  const searchItems = debounce(async (value) => {
    try {
      searchInput.value = value;
      const query = new URLSearchParams();

      if (value) {
        query.set('name', value);
      } else if (props.projectId) {
        query.set('projectId', props.projectId?.toString());
      }

      let response = await api.udc.index(query);

      // Если для порфеля проекта ещё не заданы УДК, то выводим список главных УДК
      if (props.projectId && response.udcList.items.length === 0) {
        query.delete('projectId');
        query.set('isMain', '1');
        response = await api.udc.index(query);
      }

      options.value = getOptions(response.udcList.items);
    } catch (error) {
      console.log(error);
    }
  }, 300);

  const getOptions = (udcList) => {
    return udcList.map((el) => ({
      value: el.id,
      label: `${el.notation} ${el.caption}`,
      ...el,
    }));
  };

  const addNewItem = async (value) => {
    newItem.value = null;
    await searchItems();

    if (props.items.find((el) => el.id === value)) return;

    const selectedOption = options.value.find((el) => el.id === value);
    const newList = [
      ...props.items,
      {
        id: selectedOption.id,
        notation: selectedOption.notation,
        caption: selectedOption.caption,
      },
    ];
    emit('update', newList);
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .tags_container {
    background-color: $color-bg-secondary-grey;
    flex-direction: column;
    border-radius: 7px;
    max-width: 900px;
    padding: 14px;
    width: 100%;
    margin-top: 14px;

    .tags_list {
      flex-wrap: wrap;
      gap: 4px;

      .tag {
        color: $color-text-secondary-1;
        background: $color-bg-primary-white;
        text-align: center;
        font-size: 12px;
        line-height: 1;
        border-radius: 30px;
        padding: 6px 10px;
        margin: 0 2px 4px 2px;
        white-space: nowrap;
        cursor: unset;

        &:hover {
          color: $color-text-secondary-white;
          background: $color-tag-hover;

          &:before {
            background: url('./assets/cross-white.svg') no-repeat center center
              transparent;
          }
        }
      }

      .tag-enabled {
        cursor: pointer;
        &:before {
          content: '';
          background: url('./assets/cross-black.svg') no-repeat center center
            transparent;
          width: 10px;
          height: 10px;
          margin: 0 0 0 10px;
          float: right;
        }
      }
    }

    .divider {
      border-color: $color-bg-primary-white;
    }

    .selected-header {
      font-family: Nunito Sans, sans-serif;
      font-weight: 700;
      font-size: 16px;
      line-height: 20px;
      vertical-align: middle;
    }
  }
</style>
